import { ensureValidSession, requireSupabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import { PLAN_FEATURES, PRACTICE_ADDON_CATALOG } from "@/types/practice";
import type {
    PracticeAddonBillingStatus,
    PracticeAddonCatalogState,
    PracticeAddonKey,
    PracticeFeatureKey,
    SubscriptionPlan,
} from "@/types/practice";

interface CheckoutResponse {
    success: boolean;
    type?: "checkout_session" | "subscription_updated";
    session_id?: string;
    url?: string;
    subscription_id?: string;
    plan?: string;
    error?: string;
}

interface ManageAddonCatalogItemResponse {
    addon_key: PracticeAddonKey;
    status?: PracticeAddonBillingStatus;
    is_active?: boolean;
    mutable_via_manage_addon?: boolean;
}

interface ManageAddonListResponse {
    success: boolean;
    status?: "listed";
    addons?: ManageAddonCatalogItemResponse[];
    error?: string;
}

interface ManageAddonMutationResponse {
    success: boolean;
    status?: "added" | "removed" | "already_active" | "already_removed";
    sync_source?: string;
    active_addons?: PracticeAddonKey[];
    error?: string;
}

export type ManagePracticeAddonAction = "add" | "remove";

const LOG = "[subscription.service]";
const UPGRADE_ORDER: SubscriptionPlan[] = ["starter", "professional", "enterprise"];
const CHECKOUT_TRANSPORT_ERROR = "Verbindung zum Zahlungsanbieter fehlgeschlagen";
const CHECKOUT_START_ERROR = "Checkout konnte nicht gestartet werden";
const PORTAL_OPEN_ERROR = "Portal konnte nicht geöffnet werden";
const SESSION_EXPIRED_ERROR = "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.";
const BILLING_ACCESS_DENIED_ERROR = "Sie haben keinen Zugriff auf Billing-Aktionen für diese Praxis.";
const BILLING_NOT_CONFIGURED_ERROR = "Billing ist derzeit nicht konfiguriert. Bitte Stripe-Konfiguration prüfen.";
type BillingFunctionName = "create-checkout-session" | "create-customer-portal" | "manage-addon";

function buildSessionExpiredFunctionError(): { status: number; message: string } {
    return {
        status: 401,
        message: SESSION_EXPIRED_ERROR,
    };
}

function getFunctionErrorStatus(error: unknown): number | null {
    if (!error || typeof error !== "object") return null;

    const maybeError = error as {
        context?: { status?: number } | null;
        response?: { status?: number } | null;
        status?: number;
    };

    if (typeof maybeError.context?.status === "number") return maybeError.context.status;
    if (typeof maybeError.response?.status === "number") return maybeError.response.status;
    if (typeof maybeError.status === "number") return maybeError.status;
    return null;
}

function getFunctionErrorMessage(error: unknown): string {
    if (typeof error === "string") return error;
    if (error instanceof Error) return error.message;
    if (error && typeof error === "object" && typeof (error as { message?: unknown }).message === "string") {
        return String((error as { message: string }).message);
    }

    return String(error ?? "unknown_error");
}

function isInvokeTransportFailure(error: unknown): boolean {
    const name =
        error && typeof error === "object" && typeof (error as { name?: unknown }).name === "string"
            ? String((error as { name: string }).name)
            : "";
    const message = getFunctionErrorMessage(error);
    const normalized = message.toLowerCase();
    const status = getFunctionErrorStatus(error);

    if (status !== null) return false;

    return (
        name === "FunctionsFetchError" ||
        name === "FunctionsRelayError" ||
        normalized.includes("failed to send") ||
        normalized.includes("fetch") ||
        normalized.includes("network")
    );
}

function extractErrorMessage(raw: unknown): string {
    if (!raw) return "";
    if (typeof raw === "string") return raw.trim();
    if (typeof raw !== "object") return "";

    const body = raw as Record<string, unknown>;
    const candidates = [body.message, body.error_description, body.details, body.error, body.code];

    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim()) {
            return candidate.trim();
        }

        if (candidate && typeof candidate === "object") {
            const nestedMessage = extractErrorMessage(candidate);
            if (nestedMessage) return nestedMessage;
        }
    }

    return "";
}

async function readFunctionContextMessage(rawContext: unknown): Promise<string> {
    if (!rawContext || typeof rawContext !== "object") return "";

    const context =
        typeof (rawContext as { clone?: () => unknown }).clone === "function"
            ? (rawContext as { clone: () => unknown }).clone()
            : rawContext;

    if (!context || typeof context !== "object") return "";

    const target = context as { json?: () => Promise<unknown>; text?: () => Promise<string> };

    if (typeof target.json === "function") {
        try {
            const payload = await target.json();
            const message = extractErrorMessage(payload);
            if (message) return message;
        } catch {
            // Fall back to plain-text parsing below.
        }
    }

    if (typeof target.text === "function") {
        try {
            return (await target.text()).trim();
        } catch {
            return "";
        }
    }

    return "";
}

async function resolveBillingFunctionError(error: unknown, transportFallback: string): Promise<string> {
    if (isInvokeTransportFailure(error)) {
        return transportFallback;
    }

    const status = getFunctionErrorStatus(error);
    const context = error && typeof error === "object" ? (error as { context?: unknown }).context : undefined;
    const contextMessage = await readFunctionContextMessage(context);
    const rawMessage = getFunctionErrorMessage(error).trim();
    const message = /^Edge Function returned a non-2xx status code/i.test(rawMessage) ? "" : rawMessage;

    if (status === 401) {
        return SESSION_EXPIRED_ERROR;
    }

    if (contextMessage) {
        return contextMessage;
    }

    if (status === 403) {
        return BILLING_ACCESS_DENIED_ERROR;
    }

    if (status === 503) {
        return BILLING_NOT_CONFIGURED_ERROR;
    }

    return message || transportFallback;
}

async function invokeBillingFunction<TResponse>(
    functionName: BillingFunctionName,
    body: Record<string, unknown>,
): Promise<{ data: TResponse | null; error: unknown | null }> {
    const sb = requireSupabase();
    const accessToken = await ensureValidSession();

    if (!accessToken) {
        return {
            data: null,
            error: buildSessionExpiredFunctionError(),
        };
    }

    return sb.functions.invoke(functionName, {
        body,
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

function getDefaultSuccessUrl(): string | undefined {
    if (typeof window === "undefined") return undefined;
    return `${window.location.origin}/app/settings?tab=tarif&checkout=success`;
}

function getDefaultCancelUrl(): string | undefined {
    if (typeof window === "undefined") return undefined;
    return `${window.location.origin}/app/settings?tab=tarif&checkout=cancel`;
}

function redirectToExternalUrl(url: string): void {
    if (typeof window === "undefined") return;
    window.location.assign(url);
}

function mergeAddonCatalog(
    items: ManageAddonCatalogItemResponse[] | undefined,
): PracticeAddonCatalogState[] {
    const stateByKey = new Map(items?.map((item) => [item.addon_key, item]) ?? []);

    return PRACTICE_ADDON_CATALOG.map((addon) => {
        const state = stateByKey.get(addon.key);
        const status =
            state?.status === "active" ||
            state?.status === "past_due" ||
            state?.status === "canceled"
                ? state.status
                : "inactive";

        return {
            ...addon,
            mutableViaManageAddon: state?.mutable_via_manage_addon ?? addon.mutableViaManageAddon,
            status,
            isActive: state?.is_active ?? (status === "active" || status === "past_due"),
        };
    });
}

function parseActiveAddonKeys(value: unknown): PracticeAddonKey[] | undefined {
    if (!Array.isArray(value)) {
        return undefined;
    }

    const knownAddonKeys = new Set(PRACTICE_ADDON_CATALOG.map((addon) => addon.key));
    const activeAddonKeys = value.filter(
        (entry): entry is PracticeAddonKey => typeof entry === "string" && knownAddonKeys.has(entry as PracticeAddonKey),
    );

    return activeAddonKeys.length > 0 ? activeAddonKeys : undefined;
}

export function getUpgradePlanForFeature(featureKey: PracticeFeatureKey): SubscriptionPlan | null {
    for (const plan of UPGRADE_ORDER) {
        if (PLAN_FEATURES[plan].includes(featureKey)) {
            return plan;
        }
    }

    return null;
}

export async function requestSubscriptionChange(
    plan: SubscriptionPlan,
): Promise<{ success: boolean; upgraded: boolean; redirected: boolean; error?: string }> {
    if (plan === "none") {
        return { success: false, upgraded: false, redirected: false, error: "Ungültiger Plan" };
    }

    try {
        const { data, error } = await invokeBillingFunction<CheckoutResponse>("create-checkout-session", {
            plan,
            success_url: getDefaultSuccessUrl(),
            cancel_url: getDefaultCancelUrl(),
        });

        if (error) {
            logger.error(LOG, "create-checkout-session failed", getFunctionErrorMessage(error));
            return {
                success: false,
                upgraded: false,
                redirected: false,
                error: await resolveBillingFunctionError(error, CHECKOUT_TRANSPORT_ERROR),
            };
        }

        const response = data as CheckoutResponse;
        if (!response.success) {
            return {
                success: false,
                upgraded: false,
                redirected: false,
                error: response.error || "Checkout konnte nicht gestartet werden",
            };
        }

        if (response.type === "subscription_updated") {
            return { success: true, upgraded: true, redirected: false };
        }

        if (response.type === "checkout_session" && response.url) {
            redirectToExternalUrl(response.url);
            return { success: true, upgraded: false, redirected: true };
        }

        return {
            success: false,
            upgraded: false,
            redirected: false,
            error: "Unerwartete Antwort vom Zahlungsanbieter",
        };
    } catch (error) {
        logger.error(LOG, "requestSubscriptionChange failed", error);
        return {
            success: false,
            upgraded: false,
            redirected: false,
            error: CHECKOUT_START_ERROR,
        };
    }
}

export async function openBillingPortal(): Promise<{ success: boolean; error?: string }> {
    try {
        const { data, error } = await invokeBillingFunction<{ success: boolean; url?: string; error?: string }>(
            "create-customer-portal",
            {
                return_url:
                    typeof window !== "undefined"
                        ? `${window.location.origin}/app/settings?tab=tarif`
                        : undefined,
            },
        );

        if (error) {
            logger.error(LOG, "create-customer-portal failed", getFunctionErrorMessage(error));
            return { success: false, error: await resolveBillingFunctionError(error, PORTAL_OPEN_ERROR) };
        }

        const response = data as { success: boolean; url?: string; error?: string };
        if (!response.success || !response.url) {
            return { success: false, error: response.error || "Kein Portal-Link erhalten" };
        }

        redirectToExternalUrl(response.url);
        return { success: true };
    } catch (err) {
        logger.error(LOG, "openBillingPortal failed", err);
        return { success: false, error: PORTAL_OPEN_ERROR };
    }
}

export async function listPracticeAddons(): Promise<{
    success: boolean;
    addons: PracticeAddonCatalogState[];
    error?: string;
}> {
    try {
        const { data, error } = await invokeBillingFunction<ManageAddonListResponse>("manage-addon", {
            action: "list",
        });

        if (error) {
            logger.error(LOG, "manage-addon list failed", getFunctionErrorMessage(error));
            return {
                success: false,
                addons: mergeAddonCatalog(undefined),
                error: await resolveBillingFunctionError(error, "Add-ons konnten nicht geladen werden"),
            };
        }

        const response = data as ManageAddonListResponse;
        if (!response.success) {
            return {
                success: false,
                addons: mergeAddonCatalog(undefined),
                error: response.error || "Add-ons konnten nicht geladen werden",
            };
        }

        return {
            success: true,
            addons: mergeAddonCatalog(response.addons),
        };
    } catch (error) {
        logger.error(LOG, "listPracticeAddons failed", error);
        return {
            success: false,
            addons: mergeAddonCatalog(undefined),
            error: "Add-ons konnten nicht geladen werden",
        };
    }
}

export async function managePracticeAddon(
    action: ManagePracticeAddonAction,
    addonKey: PracticeAddonKey,
): Promise<{
    success: boolean;
    status?: ManageAddonMutationResponse["status"];
    syncSource?: string;
    activeAddonKeys?: PracticeAddonKey[];
    error?: string;
}> {
    try {
        const { data, error } = await invokeBillingFunction<ManageAddonMutationResponse>("manage-addon", {
            action,
            addon_key: addonKey,
        });

        if (error) {
            logger.error(LOG, `manage-addon ${action} failed`, getFunctionErrorMessage(error));
            return {
                success: false,
                error: await resolveBillingFunctionError(error, "Addon-Aktion konnte nicht ausgeführt werden"),
            };
        }

        const response = data as ManageAddonMutationResponse;
        if (!response.success) {
            return {
                success: false,
                error: response.error || "Addon-Aktion konnte nicht ausgeführt werden",
            };
        }

        return {
            success: true,
            status: response.status,
            syncSource: typeof response.sync_source === "string" ? response.sync_source : undefined,
            activeAddonKeys: parseActiveAddonKeys(response.active_addons),
        };
    } catch (error) {
        logger.error(LOG, `managePracticeAddon ${action} failed`, error);
        return {
            success: false,
            error: "Addon-Aktion konnte nicht ausgeführt werden",
        };
    }
}
